import React, { createContext, useContext, useMemo } from 'react';
import styled from 'styled-components';

import DashboardGadgetReportLinkButton from 'components/DashboardGadgetReportLinkButton/DashboardGadgetReportLinkButton';
import CardContext from 'contexts/CardContext';
import DateInputContext from 'contexts/DateInputContext';
import usePopupCallback from 'hooks/usePopupCallback';

import { STUDIO_ELEMENT_ID_ATTR } from '../constants';

// A Composite chart's popup (on a dashboard): the report or board it opens,
// with its run-time filters and, as the popup's scope, the filters it's
// drawn with (its block's and its own)
export interface CompositeChartPopup {
  drillDowns: FilterPlateType[];
  scope: FilterPlateType[];
  reportDrillDownId?: string;
  boardDrillDownId?: string;
}

// The chart as a gadget, which the popups open from: a Composite has no
// gadget doc, so one is drawn up from its chart. Undefined without a popup.
const CompositeChartPopupContext = createContext<DashboardGadget | undefined>(
  undefined,
);

export const useCompositeChartGadget = (): DashboardGadget | undefined =>
  useContext(CompositeChartPopupContext);

export const CompositeChartPopupProvider = ({
  popup,
  children,
}: {
  popup: CompositeChartPopup | undefined;
  children: JSX.Element | JSX.Element[];
}) => {
  const { i, chartDefinition } = useContext(CardContext);
  // The chart's own dates (the popup opens on its range, as a gadget's does)
  const { dateField } = useContext(DateInputContext);
  const gadget = useMemo((): DashboardGadget | undefined => {
    if (
      !popup ||
      !chartDefinition ||
      (!popup.reportDrillDownId && !popup.boardDrillDownId)
    ) {
      return undefined;
    }
    return {
      id: i,
      chartDef: chartDefinition,
      drillDowns: popup.drillDowns,
      scope: popup.scope,
      dateField,
      reportDrillDownId: popup.reportDrillDownId,
      boardDrillDownId: popup.boardDrillDownId,
      // Never saved
      createdBy: '',
      createdOn: '',
      updatedBy: '',
      updatedOn: '',
    };
  }, [chartDefinition, dateField, i, popup]);

  return (
    <CompositeChartPopupContext.Provider value={gadget}>
      {children}
    </CompositeChartPopupContext.Provider>
  );
};

// Over the chart's top right corner, while its element is hovered
const ButtonCorner = styled.div`
  position: absolute;
  top: 2px;
  right: -6px;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.2s;

  [${STUDIO_ELEMENT_ID_ATTR}]:hover &,
  &:focus-within {
    opacity: 1;
  }
`;

const PopupButton = ({ gadget }: { gadget: DashboardGadget }) => {
  const onClick = usePopupCallback(gadget);
  if (!onClick) {
    return null;
  }
  return (
    <ButtonCorner>
      <DashboardGadgetReportLinkButton
        onClick={onClick}
        testId={`popup-${gadget.id}`}
      />
    </ButtonCorner>
  );
};

// Opens the chart's popup, as a gadget's header button does. Only a
// Composite's chart with a popup has it.
export const CompositeChartPopupButton = () => {
  const gadget = useCompositeChartGadget();
  return gadget ? <PopupButton gadget={gadget} /> : null;
};
